import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Row, Space } from 'antd'
import { PrimaryButton } from 'Components/button'
import { isFormRequired, isFormError, formValidation } from 'helpers/format'
import { CreateReq, DetailReq, UpdateReq } from 'Store/manufactuer/types'
import { createManufactuerAPI, getManufactuerDetailAPI, updateManufactuerAPI } from 'Store/manufactuer/api'
import { selectActionEvent, selectDetail, selectDetailEvent } from 'Store/manufactuer/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { AppInput, AppSelect, UploadForm } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/manufactuer/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import { ManufacurerStatus, menuManufacurerStatus } from 'helpers/constanst'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
}

const CouponDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = ['name', 'logo', 'status']
  const initForm = {
    name: '',
    logo: '',
    slug: '',
    status: ManufacurerStatus.DRAFT,
  } as CreateReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const title = isCreateMode ? 'สร้างผู้ผลิต' : 'แก้ไขผู้ผลิต'

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep({ ...initForm }))
      setFormError([])
    }
    // get banner detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    }
  }, [visible])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const form = {
        ...detail,
      } as CreateReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateReq = {
      ...formData,
      merchant_id: currentUser?.activeMerchant?.id || 0,
    }
    dispatch(createManufactuerAPI({ data: req }, currentUser.accessToken))
  }

  const onGetDetail = (): void => {
    const req: DetailReq = {
      id,
    }
    dispatch(getManufactuerDetailAPI(req, currentUser.accessToken))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateReq = {
      ...formData,
      id,
    }
    dispatch(updateManufactuerAPI({ data: req }, currentUser.accessToken))
  }

  const onUpdateData = (val: any, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  return (
    <DetailDrawer title={title} onCloseDrawer={onCloseDrawer} visible={visible} footer={FooterContent}>
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={16}>
        <Col md={24} xs={24}>
          <UploadForm
            token={currentUser.accessToken}
            onUpdateData={onUpdateData}
            currentFile={formData.logo}
            folder="manufacturer"
            listType="picture-card"
            isPrivate={true}
            acceptType="image/png,image/jpeg"
            withCropImage={true}
            aspect={1}
            name="logo"
            description="ขนาดรูปภาพที่แนะนำ คือ 150 x 150px (.png .jpeg)"
            isRequired={isRequired('logo')}
            isError={isError('logo')}
            label="รูปภาพโลโก้"
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="name"
            label="ชื่อผู้ผลิต"
            isRequired={isRequired('name')}
            isError={isError('name')}
            value={formData.name}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="slug"
            label="slug"
            isRequired={isRequired('slug')}
            isError={isError('slug')}
            value={formData.slug}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppSelect
            name="status"
            label="สถานะ"
            isRequired={isRequired('status')}
            isError={isError('status')}
            value={formData.status}
            onChange={onUpdateData}
            menu={menuManufacurerStatus}
          />
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectActionEvent(state),
    detail: selectDetail(state),
    detailEvent: selectDetailEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(CouponDetail)
